<script>
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message.js';
import ContactAPI from 'dashboard/api/contacts.js';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { isGifMessage } from 'dashboard/helper/gifMediaHelper';
import { isStickerMessage } from 'dashboard/helper/stickerMessageHelper';
import {
  isGuardedMediaUrl,
  useGuardedMedia,
} from 'dashboard/composables/useGuardedMedia';

// Aceleres 2026-07-03: redesign do Encaminhar pra replicar o WhatsApp Web.
// Modal centralizado com overlay desfocado, busca no topo, lista de contatos
// da Base de Clientes com avatar + nome + info secundária + CHECKBOX (seleção
// múltipla, o item permanece na lista marcado), e campo "Adicione uma
// mensagem..." no rodapé.
//
// Aceleres 2026-08-11: o destino deixou de ser adivinhado aqui.
// A versão anterior pegava, via /conversations, "a primeira conversa do
// contato que não fosse a atual". Isso quebrava de dois jeitos: contato sem
// nenhuma conversa (67% da base na conta 3) nem podia ser selecionado, e
// quando havia conversa ela podia estar numa conexão arquivada/desconectada —
// a mensagem era criada e nunca saía. Agora o backend devolve os destinos
// possíveis (`forward_targets`) já com o motivo de cada impedimento, o
// atendente escolhe a conexão quando há mais de uma, e o envio vai num POST
// só que responde destino a destino.
export default {
  components: { Avatar },
  setup() {
    const { guardedMediaSrc } = useGuardedMedia();
    return { guardedMediaSrc, isGuardedMediaUrl };
  },
  props: {
    show: { type: Boolean, default: false },
    conversationId: { type: Number, required: true },
    selectedMessages: { type: Array, default: () => [] },
    contactPanelMobile: { type: Boolean, default: false },
  },
  emits: ['cancel', 'update:show', 'success'],
  data() {
    return {
      searchQuery: '',
      // Resultado bruto da Base de Clientes. Itens selecionados continuam na
      // lista (checkbox marcado), igual à tela "Encaminhar para" do WhatsApp.
      rawResults: [],
      // { [contactId]: { loading, failed, options: [] } }
      targetsByContact: {},
      // { [contactId]: { optionKey, contact } } — destino escolhido.
      // Guarda o próprio contato porque a busca troca `rawResults`: sem isso
      // um contato marcado antes de digitar sumia do envio e continuava
      // contando no rodapé.
      selections: {},
      // Contato com o seletor de conexão aberto.
      expandedContactId: null,
      resolvingContactIds: [],
      additionalMessage: '',
      isSearching: false,
      isForwarding: false,
      searchTimeout: null,
      searchRequestId: 0,
      searchFailed: false,
      hasMoreContacts: false,
      nextPage: 2,
      copy: {
        dialog: 'Encaminhar mensagem',
        close: 'Fechar',
        title: 'Encaminhar mensagem para',
        selected: 'selecionado',
        selectedPlural: 'selecionados',
        searchPlaceholder: 'Buscar um nome ou número',
        loading: 'Carregando contatos…',
        loadError: 'Não foi possível carregar os contatos.',
        empty: 'Não há contatos disponíveis para encaminhamento.',
        extraPlaceholder: 'Adicione uma mensagem...',
        send: 'Encaminhar',
        recent: 'Contatos cadastrados',
        results: 'Resultados',
        chooseConnection: 'Escolha por onde enviar',
        change: 'Trocar',
        targetsError: 'Não foi possível carregar os destinos deste contato.',
        noTarget: 'Não há conexão disponível para encaminhar a este contato.',
        chooseConnectionRequired:
          'Escolha por qual conexão enviar: a conexão desta conversa não está disponível para este contato.',
      },
    };
  },
  computed: {
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    selectedCount() {
      return this.selectedMessages.length;
    },
    // Contatos selecionados continuam na lista (com checkbox marcado), igual
    // à tela "Encaminhar para" do WhatsApp.
    searchResults() {
      return this.rawResults;
    },
    destinationCount() {
      return Object.keys(this.selections).length;
    },
    isVisualMediaForward() {
      return this.selectedMessages.some(message =>
        this.isVisualMediaMessage(message)
      );
    },
    originalCaption() {
      if (this.selectedCount !== 1 || !this.isVisualMediaForward) return '';
      return String(this.primaryMessage?.content || '').trim();
    },
    allowsAdditionalMessage() {
      return this.isVisualMediaForward;
    },
    canForward() {
      return (
        this.destinationCount > 0 &&
        !this.isForwarding &&
        this.selectedCount > 0
      );
    },
    listHeading() {
      return this.searchQuery.trim() ? this.copy.results : this.copy.recent;
    },
    destinationLabel() {
      if (!this.destinationCount) return '';
      const suffix =
        this.destinationCount === 1
          ? this.copy.selected
          : this.copy.selectedPlural;
      return `${this.destinationCount} ${suffix}`;
    },
    // Nomes dos destinos escolhidos. So o rodape do celular usa: la o botao
    // "Encaminhar" fica longe da lista, e sem o nome escrito o atendente
    // perde de vista para quem esta mandando.
    selectedDestinationNames() {
      return Object.values(this.selections)
        .map(entry => this.contactName(entry.contact))
        .join(', ');
    },
    primaryMessage() {
      return this.selectedMessages[0] || null;
    },
    previewAttachment() {
      for (const message of this.selectedMessages) {
        if (!this.isVisualMediaMessage(message)) continue;
        const attachments = Array.isArray(message?.attachments)
          ? message.attachments
          : [];
        if (attachments[0]) return attachments[0];
      }
      const attachments = this.primaryMessage?.attachments;
      return Array.isArray(attachments) ? attachments[0] || null : null;
    },
    previewImageUrl() {
      if (!this.isVisualMediaForward) return '';
      const attachment = this.previewAttachment || {};
      const raw =
        attachment.thumbUrl ||
        attachment.thumb_url ||
        attachment.dataUrl ||
        attachment.data_url ||
        '';
      if (!raw) return '';
      return this.isGuardedMediaUrl(raw) ? this.guardedMediaSrc(raw) : raw;
    },
    isVideoPreview() {
      const attachment = this.previewAttachment || {};
      const fileType = String(
        attachment.fileType || attachment.file_type || ''
      ).toLowerCase();
      if (fileType === 'video') return !this.isGifPreview;
      return false;
    },
    isGifPreview() {
      return this.selectedMessages.some(message => isGifMessage(message));
    },
    previewLabel() {
      if (this.selectedCount > 1) {
        return `${this.selectedCount} mensagens`;
      }

      const content = String(this.primaryMessage?.content || '').trim();
      if (content) return content;

      const fileType =
        this.previewAttachment?.fileType || this.previewAttachment?.file_type;
      const labels = {
        image: 'Imagem',
        video: 'Vídeo',
        audio: 'Áudio',
        file: 'Documento',
      };
      return labels[fileType] || 'Mensagem';
    },
    previewIcon() {
      if (this.selectedCount > 1) return 'i-lucide-messages-square';

      const fileType =
        this.previewAttachment?.fileType || this.previewAttachment?.file_type;
      const icons = {
        image: 'i-lucide-image',
        video: 'i-lucide-video',
        audio: 'i-lucide-mic-2',
        file: 'i-lucide-file-text',
      };
      return icons[fileType] || 'i-lucide-message-square-text';
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          this.additionalMessage = this.originalCaption;
          document.removeEventListener('keydown', this.onKeydown);
          document.addEventListener('keydown', this.onKeydown);
          document.body.style.overflow = 'hidden';
          this.loadContacts('');
          // No celular o foco automatico abre o teclado por cima da lista e
          // esconde justamente os contatos que se vem escolher. Usa a mesma
          // deteccao do ReplyBox, que ja evita o teclado em toque.
          if (!this.isTouchLayout()) {
            this.$nextTick(() => this.$refs.searchInput?.focus());
          }
        } else {
          document.removeEventListener('keydown', this.onKeydown);
          document.body.style.overflow = '';
          this.reset();
        }
      },
    },
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape' && this.show) this.handleCancel();
    },
    reset() {
      clearTimeout(this.searchTimeout);
      this.searchRequestId += 1;
      this.searchQuery = '';
      this.rawResults = [];
      this.targetsByContact = {};
      this.selections = {};
      this.expandedContactId = null;
      this.resolvingContactIds = [];
      this.additionalMessage = '';
      this.isSearching = false;
      this.isForwarding = false;
      this.searchFailed = false;
      this.hasMoreContacts = false;
      this.nextPage = 2;
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchRequestId += 1;
      this.rawResults = [];
      this.isSearching = true;
      this.searchFailed = false;
      this.hasMoreContacts = false;
      this.nextPage = 2;
      const query = this.searchQuery.trim();
      this.searchTimeout = setTimeout(
        () => this.loadContacts(query),
        query ? 250 : 0
      );
    },
    extractContacts(data) {
      const payload = data?.payload;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.contacts)) return payload.contacts;
      return [];
    },
    appendUniqueContacts(currentContacts, newContacts) {
      const contactsById = new Map(
        currentContacts.map(contact => [contact.id, contact])
      );

      newContacts.forEach(contact => {
        if (contact?.id != null) contactsById.set(contact.id, contact);
      });

      return Array.from(contactsById.values());
    },
    // Uma página por vez, sob demanda. A versão anterior varria a Base inteira
    // de 15 em 15 em requisições sequenciais antes de deixar clicar em nada.
    async loadContacts(query, { append = false } = {}) {
      const requestId = append
        ? this.searchRequestId
        : this.searchRequestId + 1;
      this.searchRequestId = requestId;
      this.isSearching = true;
      this.searchFailed = false;

      const page = append ? this.nextPage : 1;

      try {
        const { data } = await ContactAPI.getForwardDirectory(page, query);
        if (requestId !== this.searchRequestId) return;

        const contacts = this.extractContacts(data);
        this.rawResults = append
          ? this.appendUniqueContacts(this.rawResults, contacts)
          : contacts;

        this.hasMoreContacts = Boolean(data?.meta?.has_more);
        this.nextPage = page + 1;
      } catch {
        if (requestId !== this.searchRequestId) return;
        if (!append) {
          this.rawResults = [];
          this.searchFailed = true;
        }
        this.hasMoreContacts = false;
      } finally {
        if (requestId === this.searchRequestId) this.isSearching = false;
      }
    },
    isVisualMediaMessage(message) {
      if (!message || isStickerMessage(message)) return false;
      if (isGifMessage(message)) return true;
      const attachments = Array.isArray(message.attachments)
        ? message.attachments
        : [];
      return attachments.some(attachment => {
        const fileType = String(
          attachment?.fileType || attachment?.file_type || ''
        ).toLowerCase();
        return fileType === 'image' || fileType === 'video';
      });
    },
    isTouchLayout() {
      return (
        window.matchMedia?.('(max-width: 767px), (pointer: coarse)').matches ??
        false
      );
    },
    onListScroll(event) {
      if (!this.hasMoreContacts || this.isSearching) return;

      const { scrollTop, clientHeight, scrollHeight } = event.target;
      if (scrollTop + clientHeight < scrollHeight - 240) return;

      this.loadContacts(this.searchQuery.trim(), { append: true });
    },
    contact(record) {
      return record?.contact || record?.meta?.sender || record || {};
    },
    contactName(record) {
      const contact = this.contact(record);
      return (
        contact.name ||
        contact.phone_number ||
        contact.phoneNumber ||
        `Contato #${record.id}`
      );
    },
    contactThumb(record) {
      const contact = this.contact(record);
      return contact.thumbnail || contact.avatar_url || contact.avatarUrl || '';
    },
    // O contato da Base de Clientes não traz a última mensagem. Priorizamos
    // telefone/e-mail e mantemos suporte ao shape antigo de conversas.
    contactSecondary(record) {
      const last =
        record?.message ||
        record?.messages?.[0] ||
        record?.last_non_activity_message;
      if (last && last.content) return String(last.content).slice(0, 80);
      const contact = this.contact(record);
      const phone = contact.phone_number || contact.phoneNumber;
      // Nomes alternativos do mesmo número entram como detalhe do contato
      // consolidado — antes cada um virava uma linha repetida na lista.
      const others = Array.isArray(contact.alternate_names)
        ? contact.alternate_names
        : [];
      const suffix = others.length
        ? ` · também salvo como ${others.join(', ')}`
        : '';
      if (phone) return `${phone}${suffix}`;
      if (contact.email) return `${contact.email}${suffix}`;
      return others.length ? others.join(', ') : '';
    },
    isSelected(contact) {
      return Boolean(this.selections[contact.id]);
    },
    isResolvingContact(contact) {
      return this.resolvingContactIds.includes(contact.id);
    },
    contactOptions(contact) {
      return this.targetsByContact[contact.id]?.options || [];
    },
    availableOptions(contact) {
      return this.contactOptions(contact).filter(option => !option.blocked);
    },
    selectedOption(contact) {
      const entry = this.selections[contact.id];
      if (!entry) return null;
      return this.contactOptions(contact).find(
        option => option.key === entry.optionKey
      );
    },
    isChosen(contact, option) {
      return this.selections[contact.id]?.optionKey === option.key;
    },
    // Rótulo do destino escolhido, mostrado embaixo do nome do contato.
    selectedOptionLabel(contact) {
      return this.selectedOption(contact)?.label || '';
    },
    isExpanded(contact) {
      return this.expandedContactId === contact.id;
    },
    // Por que este contato não pode receber, quando nenhuma opção serve.
    blockedReason(contact) {
      const entry = this.targetsByContact[contact.id];
      if (!entry || entry.loading) return '';
      if (entry.failed) return this.copy.targetsError;
      if (this.availableOptions(contact).length) return '';
      const firstBlocked = this.contactOptions(contact).find(
        option => option.blocked
      );
      return firstBlocked?.blockedReason || this.copy.noTarget;
    },
    conversationLabel(item) {
      if (item.is_source) return `${item.inbox_name} · nesta conversa`;
      if (item.assignee_name) {
        return `${item.inbox_name} · em atendimento com ${item.assignee_name}`;
      }
      return `${item.inbox_name} · conversa existente`;
    },
    normalizeTargets(payload) {
      const conversations = (payload?.conversations || []).map(item => ({
        key: `conv:${item.id}`,
        kind: 'conversation',
        conversationId: item.id,
        inboxId: item.inbox_id,
        label: this.conversationLabel(item),
        blocked: Boolean(item.blocked),
        blockedReason: item.blocked_reason || '',
        sameInboxAsSource: Boolean(item.same_inbox_as_source),
        isSource: Boolean(item.is_source),
      }));

      const inboxes = (payload?.inboxes || []).map(item => ({
        key: `inbox:${item.id}`,
        kind: 'inbox',
        inboxId: item.id,
        label: `${item.name} · abrir conversa nova`,
        blocked: Boolean(item.blocked),
        blockedReason: item.blocked_reason || '',
        sameInboxAsSource: Boolean(item.same_inbox_as_source),
      }));

      // Conversa que já existe vence sempre: abrir uma segunda com a mesma
      // pessoa criaria um thread paralelo para o mesmo chat de WhatsApp.
      // Só entre iguais é que a conexão da conversa de origem desempata.
      return [...conversations, ...inboxes].sort((a, b) => {
        if (a.blocked !== b.blocked) return a.blocked ? 1 : -1;
        if (a.isSource !== b.isSource) return a.isSource ? -1 : 1;
        if (a.kind !== b.kind) return a.kind === 'conversation' ? -1 : 1;
        if (a.sameInboxAsSource !== b.sameInboxAsSource)
          return a.sameInboxAsSource ? -1 : 1;
        return 0;
      });
    },
    async loadTargets(contact) {
      const cached = this.targetsByContact[contact.id];
      if (cached && !cached.loading && !cached.failed) return cached;

      this.targetsByContact = {
        ...this.targetsByContact,
        [contact.id]: { loading: true, failed: false, options: [] },
      };

      try {
        const { data } = await ContactAPI.getForwardTargets(
          contact.id,
          this.conversationId
        );
        const entry = {
          loading: false,
          failed: false,
          options: this.normalizeTargets(data?.payload),
        };
        this.targetsByContact = {
          ...this.targetsByContact,
          [contact.id]: entry,
        };
        return entry;
      } catch {
        const entry = { loading: false, failed: true, options: [] };
        this.targetsByContact = {
          ...this.targetsByContact,
          [contact.id]: entry,
        };
        return entry;
      }
    },
    chooseOption(contact, option) {
      if (option.blocked) return;
      this.selections = {
        ...this.selections,
        [contact.id]: { optionKey: option.key, contact },
      };
      this.expandedContactId = null;
    },
    unselectContact(contact) {
      const next = { ...this.selections };
      delete next[contact.id];
      this.selections = next;
      if (this.expandedContactId === contact.id) this.expandedContactId = null;
    },
    toggleExpanded(contact) {
      this.expandedContactId = this.isExpanded(contact) ? null : contact.id;
    },
    async toggleContact(contact) {
      if (this.isSelected(contact)) {
        this.unselectContact(contact);
        return;
      }

      if (this.isResolvingContact(contact)) return;

      this.resolvingContactIds = [...this.resolvingContactIds, contact.id];
      try {
        const entry = await this.loadTargets(contact);
        if (entry.failed) {
          useAlert(this.copy.targetsError);
          return;
        }

        const available = entry.options.filter(option => !option.blocked);
        if (!available.length) {
          const firstBlocked = entry.options.find(option => option.blocked);
          useAlert(firstBlocked?.blockedReason || this.copy.noTarget);
          return;
        }

        // Só escolhe sozinho um destino seguro: uma conversa que já existe, ou
        // abrir conversa na MESMA conexão de onde a mensagem saiu. Escolher
        // outra instância por conta própria foi o que mandou um encaminhamento
        // por uma conexão sem status confirmado e devolveu 404 do provedor.
        const best = available[0];
        if (best.kind !== 'conversation' && !best.sameInboxAsSource) {
          this.expandedContactId = contact.id;
          useAlert(this.copy.chooseConnectionRequired);
          return;
        }

        this.selections = {
          ...this.selections,
          [contact.id]: { optionKey: best.key, contact },
        };
        this.expandedContactId = null;
      } finally {
        this.resolvingContactIds = this.resolvingContactIds.filter(
          id => id !== contact.id
        );
      }
    },
    buildDestinations() {
      return Object.values(this.selections)
        .map(({ contact }) => {
          const option = this.selectedOption(contact);
          if (!option) return null;
          return option.kind === 'conversation'
            ? { target_conversation_id: option.conversationId }
            : { contact_id: contact.id, inbox_id: option.inboxId };
        })
        .filter(Boolean);
    },
    reportForwardResults(results) {
      const okCount = results.filter(result => result.ok).length;
      const failed = results.filter(result => !result.ok);

      if (!failed.length) {
        useAlert(this.$t('CONVERSATION.FORWARD.SUCCESS'));
        this.$emit('success');
        this.localShow = false;
        return;
      }

      if (okCount > 0) {
        useAlert(
          `Encaminhado para ${okCount} de ${results.length} destinos. ${failed[0].error || ''}`.trim()
        );
        this.$emit('success');
        this.localShow = false;
        return;
      }

      // Nenhum destino recebeu: o modal continua aberto com o motivo real.
      useAlert(failed[0]?.error || this.$t('CONVERSATION.FORWARD.ERROR'));
    },
    async handleForward() {
      if (!this.canForward) return;

      const destinations = this.buildDestinations();
      if (!destinations.length) return;

      this.isForwarding = true;
      try {
        const { data } = await MessageApi.forward(this.conversationId, {
          destinations,
          messageIds: this.selectedMessages.map(message => message.id),
          additionalMessage: this.allowsAdditionalMessage
            ? this.additionalMessage.trim()
            : '',
          replaceCaption: Boolean(this.originalCaption),
        });
        this.reportForwardResults(data?.results || []);
      } catch (error) {
        const results = error?.response?.data?.results;
        if (Array.isArray(results) && results.length) {
          this.reportForwardResults(results);
        } else {
          useAlert(
            error?.response?.data?.error ||
              this.$t('CONVERSATION.FORWARD.ERROR')
          );
        }
      } finally {
        this.isForwarding = false;
      }
    },
    handleCancel() {
      if (this.isForwarding) return;
      this.localShow = false;
      this.$emit('cancel');
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <Transition name="acl-fwd-fade">
      <div
        v-if="show"
        class="acl-forward-overlay"
        :class="{
          'acl-forward-overlay--contact-panel-mobile': contactPanelMobile,
        }"
        @click.self="handleCancel"
      >
        <div
          class="acl-forward-modal acl-forward-modal--forward"
          :class="{
            'acl-forward-modal--idle': destinationCount === 0,
            'acl-forward-modal--media': isVisualMediaForward,
          }"
          role="dialog"
          aria-modal="true"
          :aria-label="copy.dialog"
        >
          <!-- Header -->
          <div class="acl-forward-header">
            <button
              type="button"
              class="acl-forward-close"
              :aria-label="copy.close"
              @click="handleCancel"
            >
              <span class="i-lucide-x" />
            </button>
            <div class="acl-forward-header__titles">
              <h2 class="acl-forward-title">{{ copy.title }}</h2>
              <span v-if="destinationCount > 0" class="acl-forward-subtitle">
                {{ destinationLabel }}
              </span>
            </div>
          </div>

          <!-- Busca -->
          <div class="acl-forward-search">
            <span class="i-lucide-search acl-forward-search__icon" />
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="acl-forward-search__input"
              :placeholder="copy.searchPlaceholder"
              autocomplete="off"
              @input="onSearchInput"
            />
            <span
              v-if="isSearching"
              class="i-lucide-loader acl-forward-search__spinner"
            />
          </div>

          <!-- Lista da Base de Clientes -->
          <div class="acl-forward-list" @scroll.passive="onListScroll">
            <div class="acl-forward-list__heading">
              {{ listHeading }}
            </div>
            <div
              v-if="isSearching && searchResults.length === 0"
              class="acl-forward-empty"
            >
              <span class="i-lucide-loader acl-forward-empty__spinner" />
              {{ copy.loading }}
            </div>
            <div v-else-if="searchFailed" class="acl-forward-empty">
              {{ copy.loadError }}
            </div>
            <div
              v-else-if="searchResults.length === 0"
              class="acl-forward-empty"
            >
              {{ copy.empty }}
            </div>
            <div
              v-for="contactRecord in searchResults"
              :key="contactRecord.id"
              class="acl-forward-row"
            >
              <button
                type="button"
                class="acl-forward-item"
                :class="{
                  'acl-forward-item--selected': isSelected(contactRecord),
                }"
                :disabled="isResolvingContact(contactRecord) || isForwarding"
                :aria-busy="isResolvingContact(contactRecord)"
                @click="toggleContact(contactRecord)"
              >
                <span
                  class="acl-forward-check"
                  :class="{
                    'acl-forward-check--on': isSelected(contactRecord),
                  }"
                  aria-hidden="true"
                >
                  <span
                    v-if="isResolvingContact(contactRecord)"
                    class="i-lucide-loader animate-spin"
                  />
                  <span
                    v-else-if="isSelected(contactRecord)"
                    class="i-lucide-check"
                  />
                </span>
                <Avatar
                  :name="contactName(contactRecord)"
                  :src="contactThumb(contactRecord)"
                  :size="48"
                  rounded-full
                />
                <div class="acl-forward-item__info">
                  <span class="acl-forward-item__name">{{
                    contactName(contactRecord)
                  }}</span>
                  <span
                    v-if="contactSecondary(contactRecord)"
                    class="acl-forward-item__secondary"
                  >
                    {{ contactSecondary(contactRecord) }}
                  </span>
                  <!-- Destino escolhido / motivo do impedimento: o atendente
                       sabe para onde vai antes de clicar em Encaminhar. -->
                  <span
                    v-if="isSelected(contactRecord)"
                    class="acl-forward-item__target"
                  >
                    {{ selectedOptionLabel(contactRecord) }}
                  </span>
                  <span
                    v-else-if="blockedReason(contactRecord)"
                    class="acl-forward-item__blocked"
                  >
                    {{ blockedReason(contactRecord) }}
                  </span>
                </div>
              </button>

              <button
                v-if="
                  isSelected(contactRecord) &&
                  availableOptions(contactRecord).length > 1
                "
                type="button"
                class="acl-forward-change"
                :disabled="isForwarding"
                @click="toggleExpanded(contactRecord)"
              >
                {{ copy.change }}
              </button>

              <div v-if="isExpanded(contactRecord)" class="acl-forward-options">
                <div class="acl-forward-options__heading">
                  {{ copy.chooseConnection }}
                </div>
                <button
                  v-for="option in contactOptions(contactRecord)"
                  :key="option.key"
                  type="button"
                  class="acl-forward-option"
                  :class="{
                    'acl-forward-option--active': isChosen(
                      contactRecord,
                      option
                    ),
                    'acl-forward-option--blocked': option.blocked,
                  }"
                  :disabled="option.blocked || isForwarding"
                  @click="chooseOption(contactRecord, option)"
                >
                  <span class="acl-forward-option__label">
                    {{ option.label }}
                  </span>
                  <span
                    v-if="option.blocked"
                    class="acl-forward-option__reason"
                  >
                    {{ option.blockedReason }}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Footer: prévia da mensagem + comentário opcional + enviar.
               No celular ele sobe deslizando assim que o primeiro destino é
               marcado — quem comanda é a classe `--idle` na raiz do modal,
               porque a lista também reage (abre espaço no rodapé). -->
          <div class="acl-forward-footer">
            <div v-if="isVisualMediaForward" class="acl-forward-composer">
              <div class="acl-forward-preview" :title="previewLabel">
                <img
                  v-if="previewImageUrl"
                  :src="previewImageUrl"
                  :alt="previewLabel"
                  class="acl-forward-preview__image"
                />
                <div v-else class="acl-forward-preview__fallback">
                  <span :class="previewIcon" />
                </div>
                <span
                  v-if="isVideoPreview"
                  class="acl-forward-preview__play"
                  aria-hidden="true"
                >
                  <span class="i-lucide-play" />
                </span>
                <span
                  v-if="selectedCount > 1"
                  class="acl-forward-preview__count"
                >
                  {{ selectedCount }}
                </span>
              </div>
              <textarea
                v-model="additionalMessage"
                class="acl-forward-extra"
                :placeholder="copy.extraPlaceholder"
                rows="2"
                :disabled="isForwarding"
                @keydown.ctrl.enter.prevent="handleForward"
                @keydown.meta.enter.prevent="handleForward"
              />
            </div>

            <div v-if="destinationCount > 0" class="acl-forward-actionbar">
              <span
                class="acl-forward-actionbar__names"
                :title="selectedDestinationNames"
              >
                {{ selectedDestinationNames }}
              </span>
              <button
                type="button"
                class="acl-forward-send"
                :class="{ 'acl-forward-send--disabled': !canForward }"
                :disabled="!canForward"
                :aria-label="copy.send"
                @click="handleForward"
              >
                <span
                  v-if="isForwarding"
                  class="i-lucide-loader acl-forward-send__spinner"
                />
                <span v-else class="i-lucide-send-horizontal" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped src="./contactPickerModal.css"></style>
